/**
 * 桑基图数据服务
 * 用于处理专业数据接口调用和数据转换
 */
import request from '@/http/request'

/**
 * 专业数据API服务类
 */
class SankeyDataService {
  /**
   * 获取专业及二级子专业列表
   * @param {number} parentId - 父级id，不传按照tree格式返回一二级数据
   * @returns {Promise} 返回专业树形数据
   */
  async getMajorList(parentId = null) {
    try {
      const params = {}
      if (parentId !== null) {
        params.parent_id = parentId
      }
      
      const result = await request('/api/Subspecialty/getMajorList', params, 'post')
      return result
    } catch (error) {
      console.error('获取专业列表失败:', error)
      throw error
    }
  }

  /**
   * 获取第三层全部专业以及分值数据
   * @param {number} parentId - 第二级的id，不传默认返回全部第三级的数据
   * @returns {Promise} 返回第三级专业数据
   */
  async getSubspecialtyList(parentId = null) {
    try {
      const params = {}
      if (parentId !== null) {
        params.parent_id = parentId
      }
      
      const result = await request('/api/Subspecialty/getSubspecialtyList', params, 'post')
      return result
    } catch (error) {
      console.error('获取子专业列表失败:', error)
      throw error
    }
  }

  /**
   * 获取子专业详细信息
   * @param {number} subId - 子专业ID
   * @returns {Promise} 返回子专业详细信息
   */
  async getSubspecialtyInfo(subId) {
    try {
      const response = await request('/api/Subspecialty/getSubspecialtyInfo', { sub_id: subId }, 'post')
      // 适配新的API数据结构：数据现在在response.result中
      const result = response.result || response
      return result
    } catch (error) {
      console.error('获取子专业详细信息失败:', error)
      throw error
    }
  }

  /**
   * 解析分值详情字符串
   * @param {string} scoreDetailsStr - 分值详情JSON字符串
   * @returns {Object} 解析后的分值对象（扁平化结构）
   */
  parseScoreDetails(scoreDetailsStr) {
    if (!scoreDetailsStr) return null
    
    try {
      let scoreData = {}
      if (typeof scoreDetailsStr === 'string') {
        scoreData = JSON.parse(scoreDetailsStr)
      } else {
        scoreData = scoreDetailsStr
      }
      
      // 创建扁平化的分数对象，先保留顶层字段（如 balance_score 等可能在顶层）
      const extractedScores = { ...scoreData }
      
      // 从嵌套结构中提取分数数据
      // 需求满足相关分数
      if (scoreData.requirement_met) {
        if (scoreData.requirement_met.lemployment_oriented) {
          extractedScores.lemployment_oriented_score = scoreData.requirement_met.lemployment_oriented.score
        }
        if (scoreData.requirement_met.higher_school) {
          extractedScores.higher_school_score = scoreData.requirement_met.higher_school.score
        }
        if (scoreData.requirement_met.interest_oriented) {
          extractedScores.interest_oriented_score = scoreData.requirement_met.interest_oriented.score
        }
      }
      
      // 条件适配相关分数
      if (scoreData.condition_adaptation) {
        if (scoreData.condition_adaptation.score_adaptation) {
          extractedScores.score_adaptation_score = scoreData.condition_adaptation.score_adaptation.score
        }
        if (scoreData.condition_adaptation.disciplinary_adaptation) {
          extractedScores.disciplinary_adaptation_score = scoreData.condition_adaptation.disciplinary_adaptation.score
        }
        if (scoreData.condition_adaptation.resource_adaptation) {
          extractedScores.resource_adaptation_score = scoreData.condition_adaptation.resource_adaptation.score
        }
      }
      
      // 场景对接相关分数
      if (scoreData.scene_docking) {
        if (scoreData.scene_docking.regional_docking) {
          extractedScores.regional_docking_score = scoreData.scene_docking.regional_docking.score
        }
        if (scoreData.scene_docking.industry_docking) {
          extractedScores.industry_docking_score = scoreData.scene_docking.industry_docking.score
        }
        if (scoreData.scene_docking.policy_oriented) {
          extractedScores.policy_oriented_score = scoreData.scene_docking.policy_oriented.score
        }
      }
      
      // 数据支撑相关分数
      if (scoreData.data_support) {
        if (scoreData.data_support.employment_support) {
          extractedScores.employment_support_score = scoreData.data_support.employment_support.score
        }
        if (scoreData.data_support.higherschool_support) {
          extractedScores.higherschool_support_score = scoreData.data_support.higherschool_support.score
        }
      }
      
      // 学习特征相关分数
      if (scoreData.learning_features) {
        if (scoreData.learning_features.learning_difficulty) {
          extractedScores.learning_difficulty_score = scoreData.learning_features.learning_difficulty.score
        }
        if (scoreData.learning_features.time_investment) {
          extractedScores.time_investment_score = scoreData.learning_features.time_investment.score
        }
        if (scoreData.learning_features.skill_requirements) {
          extractedScores.skill_requirements_score = scoreData.learning_features.skill_requirements.score
        }
      }
      
      // 发展特征相关分数
      if (scoreData.development_characteristics) {
        if (scoreData.development_characteristics.industry_lifecycle) {
          extractedScores.industry_lifecycle_score = scoreData.development_characteristics.industry_lifecycle.score
        }
        if (scoreData.development_characteristics.salary_growth) {
          extractedScores.salary_growth_score = scoreData.development_characteristics.salary_growth.score
        }
        if (scoreData.development_characteristics.job_adaptation) {
          extractedScores.job_adaptation_score = scoreData.development_characteristics.job_adaptation.score
        }
      }
      
      // 风险特征相关分数
      if (scoreData.risk_characteristics) {
        if (scoreData.risk_characteristics.industry_risk) {
          extractedScores.industry_risk_score = scoreData.risk_characteristics.industry_risk.score
        }
        if (scoreData.risk_characteristics.difficulty_of_transformation) {
          extractedScores.difficulty_of_transformation_score = scoreData.risk_characteristics.difficulty_of_transformation.score
        }
        if (scoreData.risk_characteristics.alternative_risk) {
          extractedScores.alternative_risk_score = scoreData.risk_characteristics.alternative_risk.score
        }
      }
      
      // 适配特征相关分数
      if (scoreData.adaptation_features) {
        if (scoreData.adaptation_features.interest_matching) {
          extractedScores.interest_matching_score = scoreData.adaptation_features.interest_matching.score
        }
        if (scoreData.adaptation_features.lifestyle_adaptation) {
          extractedScores.lifestyle_adaptation_score = scoreData.adaptation_features.lifestyle_adaptation.score
        }
      }
      
      // 移除嵌套对象，只保留扁平化的分数字段
      // 保留顶层分数字段（如 balance_score, faxue_score 等）和从嵌套结构中提取的分数
      const flatScores = {}
      for (const key in extractedScores) {
        const value = extractedScores[key]
        // 跳过嵌套对象（已经在上面提取了）
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          continue
        }
        // 保留数字类型的分数字段，或可转换为数字的字符串
        if (typeof value === 'number') {
          flatScores[key] = value
        } else if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
          flatScores[key] = parseFloat(value)
        }
      }
      
      return flatScores
    } catch (error) {
      console.error('解析分值详情失败:', error)
      return null
    }
  }

  /**
   * 获取分值字段的中文名称映射
   * @returns {Object} 分值字段中文名称映射
   */
  getScoreFieldNames() {
    return {
      faxue_score: '法学',
      lishi_score: '历史学',
      lixue_score: '理学',
      yishu_score: '艺术学',
      yixue_score: '医学',
      guanli_score: '管理学',
      jiaoyu_score: '教育学',
      jingji_score: '经济学',
      wenxue_score: '文学',
      zhexue_score: '哲学',
      balance_score: '平衡性',
      gongxue_score: '工学',
      nongxue_score: '农学',
      higher_school_score: '高校支持度',
      industry_risk_score: '行业风险',
      salary_growth_score: '薪资增长',
      theory_driven_score: '理论驱动',
      job_adaptation_score: '就业适应性',
      low_abstraction_score: '低抽象度',
      mid_abstraction_score: '中抽象度',
      policy_oriented_score: '政策导向',
      time_investment_score: '时间投入',
      alternative_risk_score: '替代风险',
      high_abstraction_score: '高抽象度',
      industry_docking_score: '产业对接',
      regional_docking_score: '区域对接',
      score_adaptation_score: '分数适应性',
      interest_matching_score: '兴趣匹配',
      interest_oriented_score: '兴趣导向',
      employment_support_score: '就业支持',
      industry_lifecycle_score: '行业生命周期',
      skill_requirements_score: '技能要求',
      learning_difficulty_score: '学习难度',
      resource_adaptation_score: '资源适应性',
      higherschool_support_score: '高校支持',
      lemployment_oriented_score: '就业导向',
      lifestyle_adaptation_score: '生活方式适应',
      disciplinary_adaptation_score: '学科适应性',
      difficulty_of_transformation_score: '转型难度'
    }
  }

  /**
   * 将专业数据转换为桑基图数据格式
   * @param {Array} majorList - 专业列表数据
   * @param {Array} subspecialtyList - 子专业列表数据
   * @param {string} scoreField - 要展示的分值字段
   * @returns {Object} 桑基图数据格式 {nodes, links}
   */
  convertToSankeyData(majorList, subspecialtyList, scoreField = 'balance_score') {
    const nodes = new Set()
    const links = []
    const scoreFieldNames = this.getScoreFieldNames()
    
    // 创建节点映射
    const majorMap = new Map()
    const categoryMap = new Map()
    
    // 处理一级专业门类和二级专业类
    majorList.forEach(category => {
      const categoryName = category.major_name
      nodes.add(categoryName)
      categoryMap.set(category.major_id, categoryName)
      
      if (category.children && category.children.length > 0) {
        category.children.forEach(major => {
          const majorName = major.major_name
          nodes.add(majorName)
          majorMap.set(major.major_id, majorName)
          
          // 创建从门类到专业类的连接
          links.push({
            source: categoryName,
            target: majorName,
            value: 1 // 这里可以根据实际需求调整权重
          })
        })
      }
    })
    
    // 处理三级具体专业
    subspecialtyList.forEach(subspecialty => {
      const subspecialtyName = subspecialty.sub_name.trim()
      nodes.add(subspecialtyName)
      
      // 获取对应的二级专业
      const majorName = majorMap.get(subspecialty.parent_id)
      if (majorName) {
        // 解析分值详情
        const scoreDetails = this.parseScoreDetails(subspecialty.score_details)
        let scoreValue = 1 // 默认值
        
        // 优先使用顶层的 score 字段（如果 scoreField 是 balance_score 或未指定）
        if (scoreField === 'balance_score' && subspecialty.score) {
          scoreValue = parseFloat(subspecialty.score) || 1
        } else if (scoreDetails && scoreDetails[scoreField]) {
          // 从 score_details 中获取指定字段
          scoreValue = parseFloat(scoreDetails[scoreField]) || 1
        } else if (subspecialty.score) {
          // 如果找不到指定的分数字段，使用顶层的 score 字段作为后备
          scoreValue = parseFloat(subspecialty.score) || 1
        }
        
        // 创建从专业类到具体专业的连接
        links.push({
          source: majorName,
          target: subspecialtyName,
          value: scoreValue
        })
      }
    })
    
    return {
      nodes: Array.from(nodes).map(name => ({ name })),
      links,
      scoreField: scoreFieldNames[scoreField] || scoreField
    }
  }

  /**
   * 获取完整的桑基图数据
   * @param {string} scoreField - 要展示的分值字段
   * @returns {Promise<Object>} 桑基图数据
   */
  async getSankeyData(scoreField = 'balance_score') {
    try {
      // 并行获取两个接口的数据
      const [majorList, subspecialtyList] = await Promise.all([
        this.getMajorList(),
        this.getSubspecialtyList()
      ])
      
      // 转换为桑基图数据格式
      const sankeyData = this.convertToSankeyData(majorList, subspecialtyList, scoreField)
      
      return {
        success: true,
        data: sankeyData,
        rawData: {
          majorList,
          subspecialtyList
        }
      }
    } catch (error) {
      console.error('获取桑基图数据失败:', error)
      return {
        success: false,
        error: error.message,
        data: null
      }
    }
  }

  /**
   * 获取可用的分值字段列表
   * @returns {Array} 分值字段列表
   */
  getAvailableScoreFields() {
    const scoreFieldNames = this.getScoreFieldNames()
    return Object.keys(scoreFieldNames).map(key => ({
      value: key,
      label: scoreFieldNames[key]
    }))
  }

  /**
   * 根据专业门类筛选数据
   * @param {Array} majorList - 专业列表
   * @param {Array} subspecialtyList - 子专业列表
   * @param {number} categoryId - 专业门类ID
   * @param {string} scoreField - 分值字段
   * @returns {Object} 筛选后的桑基图数据
   */
  filterByCategoryId(majorList, subspecialtyList, categoryId, scoreField = 'balance_score') {
    // 找到指定的专业门类
    const targetCategory = majorList.find(category => category.major_id === categoryId)
    if (!targetCategory) {
      return { nodes: [], links: [], scoreField }
    }
    
    // 获取该门类下的所有二级专业ID
    const majorIds = targetCategory.children ? targetCategory.children.map(major => major.major_id) : []
    
    // 筛选出属于这些二级专业的三级专业
    const filteredSubspecialty = subspecialtyList.filter(subspecialty => 
      majorIds.includes(subspecialty.parent_id)
    )
    
    // 只保留相关的专业门类数据
    const filteredMajorList = [targetCategory]
    
    return this.convertToSankeyData(filteredMajorList, filteredSubspecialty, scoreField)
  }

  /**
   * 根据专业类ID筛选桑基图数据
   * @param {Array} majorList - 专业门类列表
   * @param {Array} subspecialtyList - 子专业列表
   * @param {number} subcategoryId - 专业类ID（二级专业ID）
   * @param {string} scoreField - 分值字段
   * @returns {Object} 筛选后的桑基图数据
   */
  filterBySubcategoryId(majorList, subspecialtyList, subcategoryId, scoreField = 'balance_score') {
    // 找到指定的专业类所属的专业门类
    let targetCategory = null
    let targetSubcategory = null
    
    for (const category of majorList) {
      if (category.children) {
        const subcategory = category.children.find(child => child.major_id === subcategoryId)
        if (subcategory) {
          targetCategory = category
          targetSubcategory = subcategory
          break
        }
      }
    }
    
    if (!targetCategory || !targetSubcategory) {
      return { nodes: [], links: [], scoreField }
    }
    
    // 筛选出属于这个专业类的三级专业
    const filteredSubspecialty = subspecialtyList.filter(subspecialty => 
      subspecialty.parent_id === subcategoryId
    )
    
    // 构建筛选后的专业门类数据，只包含选中的专业类
    const filteredMajorList = [{
      ...targetCategory,
      children: [targetSubcategory]
    }]
    
    return this.convertToSankeyData(filteredMajorList, filteredSubspecialty, scoreField)
  }

  /**
   * 根据具体专业名称列表筛选桑基图数据
   * @param {Array} majorList - 专业门类列表
   * @param {Array} subspecialtyList - 子专业列表
   * @param {Array} subspecialtyIds - 具体专业ID列表
   * @param {string} scoreField - 分值字段
   * @returns {Object} 筛选后的桑基图数据
   */
  filterBySubspecialtyIds(majorList, subspecialtyList, subspecialtyIds, scoreField = 'balance_score') {
    if (!subspecialtyIds || subspecialtyIds.length === 0) {
      return { nodes: [], links: [], scoreField }
    }
    
    // 筛选出匹配的具体专业
    const filteredSubspecialty = subspecialtyList.filter(subspecialty => 
      subspecialtyIds.includes(subspecialty.major_id)
    )
    
    if (filteredSubspecialty.length === 0) {
      return { nodes: [], links: [], scoreField }
    }
    
    // 收集所有相关的二级专业ID和一级专业ID
    const subcategoryIds = new Set()
    const categoryIds = new Set()
    
    filteredSubspecialty.forEach(subspecialty => {
      subcategoryIds.add(subspecialty.parent_id)
    })
    
    // 找到所有相关的专业门类和专业类
    const filteredMajorList = []
    majorList.forEach(category => {
      if (category.children) {
        const relevantChildren = category.children.filter(child => 
          subcategoryIds.has(child.major_id)
        )
        if (relevantChildren.length > 0) {
          filteredMajorList.push({
            ...category,
            children: relevantChildren
          })
        }
      }
    })
    
    return this.convertToSankeyData(filteredMajorList, filteredSubspecialty, scoreField)
  }

  /**
   * 获取专业统计信息
   * @param {Array} majorList - 专业列表
   * @param {Array} subspecialtyList - 子专业列表
   * @returns {Object} 统计信息
   */
  getStatistics(majorList, subspecialtyList) {
    const stats = {
      totalCategories: majorList.length,
      totalMajors: 0,
      totalSubspecialties: subspecialtyList.length,
      categoriesWithScores: 0,
      subspecialtiesWithScores: 0
    }
    
    // 统计二级专业数量
    majorList.forEach(category => {
      if (category.children) {
        stats.totalMajors += category.children.length
      }
    })
    
    // 统计有分值的专业数量
    stats.subspecialtiesWithScores = subspecialtyList.filter(
      subspecialty => subspecialty.score_details
    ).length
    
    return stats
  }

  /**
   * 根据分值详情计算属性类型和分值
   * @param {Object} scoreDetails - 分值详情对象
   * @returns {Object} 属性分析结果
   */
  analyzeAttributes(scoreDetails) {
    if (!scoreDetails) return null

    // 辅助函数：安全解析数字
    const parseNum = (val) => {
      const num = parseFloat(val)
      return isNaN(num) ? 0 : num
    }

    // 形态属性相关字段（根据实际API返回的字段，这里可能需要调整）
    // 由于API示例中没有这些字段，我们先用平均值或默认值
    const morphologicalFields = {
      concept_score: parseNum(scoreDetails.concept_score),
      supporter_score: parseNum(scoreDetails.supporter_score),
      abstract_level_score: parseNum(scoreDetails.abstract_level_score)
    }

    // 功能属性相关字段
    const functionalFields = {
      requirement_met_score: parseNum(scoreDetails.requirement_met_score),
      condition_adaptation_score: parseNum(scoreDetails.condition_adaptation_score),
      scene_docking_score: parseNum(scoreDetails.scene_docking_score),
      data_support_score: parseNum(scoreDetails.data_support_score)
    }

    // 特征属性相关字段
    const featureFields = {
      learning_features_score: parseNum(scoreDetails.learning_features_score),
      development_characteristics_score: parseNum(scoreDetails.development_characteristics_score),
      risk_characteristics_score: parseNum(scoreDetails.risk_characteristics_score),
      adaptation_features_score: parseNum(scoreDetails.adaptation_features_score)
    }

    // 计算各属性总分
    // 如果API返回了总分，优先使用；否则计算平均值
    let morphological_score = parseNum(scoreDetails.morphological_attribute_score)
    if (morphological_score === 0) {
      // 如果没有总分，用相关字段计算
      const values = Object.values(morphologicalFields).filter(v => v > 0)
      if (values.length > 0) {
        morphological_score = values.reduce((a, b) => a + b, 0) / values.length
      } else {
        // 如果没有任何相关字段，使用抽象程度相关字段估算
        const low = parseNum(scoreDetails.low_abstraction_score)
        const mid = parseNum(scoreDetails.mid_abstraction_score)
        const high = parseNum(scoreDetails.high_abstraction_score)
        morphological_score = (low + mid + high) / 3 || 5
      }
    }
    
    let functional_score = parseNum(scoreDetails.functional_attribute_score)
    if (functional_score === 0) {
      const values = Object.values(functionalFields).filter(v => v > 0)
      if (values.length > 0) {
        functional_score = values.reduce((a, b) => a + b, 0) / values.length
      } else {
        // 使用需求满足、条件适配、场景对接相关的字段估算
        const employment = parseNum(scoreDetails.lemployment_oriented_score)
        const school = parseNum(scoreDetails.higher_school_score)
        const interest = parseNum(scoreDetails.interest_oriented_score)
        const score_adapt = parseNum(scoreDetails.score_adaptation_score)
        const regional = parseNum(scoreDetails.regional_docking_score)
        const industry = parseNum(scoreDetails.industry_docking_score)
        const policy = parseNum(scoreDetails.policy_oriented_score)
        const values = [employment, school, interest, score_adapt, regional, industry, policy].filter(v => v > 0)
        functional_score = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 5
      }
    }
    
    let feature_score = parseNum(scoreDetails.feature_attribute_score)
    if (feature_score === 0) {
      const values = Object.values(featureFields).filter(v => v > 0)
      if (values.length > 0) {
        feature_score = values.reduce((a, b) => a + b, 0) / values.length
      } else {
        // 使用学习、发展、风险、适配相关字段估算
        const learning = parseNum(scoreDetails.learning_difficulty_score)
        const time = parseNum(scoreDetails.time_investment_score)
        const skill = parseNum(scoreDetails.skill_requirements_score)
        const salary = parseNum(scoreDetails.salary_growth_score)
        const risk = parseNum(scoreDetails.industry_risk_score)
        const values = [learning, time, skill, salary, risk].filter(v => v > 0)
        feature_score = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 5
      }
    }

    return {
      // 三大属性
      morphological: { name: '形态属性', score: morphological_score, fields: morphologicalFields },
      functional: { name: '功能属性', score: functional_score, fields: functionalFields },
      feature: { name: '特征属性', score: feature_score, fields: featureFields },
      
      // 主导类型
      drivenType: scoreDetails.driven_type || 'balance',
      
      // 抽象程度
      abstractionScore: parseNum(scoreDetails.abstraction_score)
    }
  }

  /**
   * 获取分属性的映射关系
   * @returns {Object} 分属性映射
   */
  getSubAttributeMapping() {
    return {
      morphological: {
        name: '形态属性',
        subAttributes: [
          { key: 'concept_score', name: '概念类型', category: '内容载体' },
          { key: 'supporter_score', name: '内容载体', category: '内容载体' },
          { key: 'abstract_level_score', name: '抽象程度定义', category: '抽象程度定义' }
        ]
      },
      functional: {
        name: '功能属性',
        subAttributes: [
          { key: 'requirement_met_score', name: '需求满足得分', category: '需求满足得分' },
          { key: 'condition_adaptation_score', name: '条件适配得分', category: '条件适配得分' },
          { key: 'scene_docking_score', name: '场景对接得分', category: '场景对接得分' },
          { key: 'data_support_score', name: '数据支撑得分', category: '数据支撑得分' }
        ]
      },
      feature: {
        name: '特征属性',
        subAttributes: [
          { key: 'learning_features_score', name: '学习特征得分', category: '学习特征得分' },
          { key: 'development_characteristics_score', name: '发展特征得分', category: '发展特征得分' },
          { key: 'risk_characteristics_score', name: '风险特征得分', category: '风险特征得分' },
          { key: 'adaptation_features_score', name: '适配特征得分', category: '适配特征得分' }
        ]
      }
    }
  }

  /**
   * 获取细分项的映射关系
   * @returns {Object} 细分项映射
   */
  getDetailedItemsMapping() {
    return {
      需求满足得分: [
        { key: 'lemployment_oriented_score', name: '就业导向' },
        { key: 'higher_school_score', name: '升学导向' },
        { key: 'interest_oriented_score', name: '兴趣导向' }
      ],
      条件适配得分: [
        { key: 'score_adaptation_score', name: '分数适配' },
        { key: 'disciplinary_adaptation_score', name: '学科优势适配' },
        { key: 'resource_adaptation_score', name: '资源背景适配' }
      ],
      场景对接得分: [
        { key: 'regional_docking_score', name: '地域场景对接' },
        { key: 'industry_docking_score', name: '行业场景对接' },
        { key: 'policy_oriented_score', name: '政策场景对接' }
      ],
      数据支撑得分: [
        { key: 'employment_support_score', name: '就业数据支撑' },
        { key: 'higherschool_support_score', name: '升学数据支撑' }
      ],
      学习特征得分: [
        { key: 'learning_difficulty_score', name: '学习难度' },
        { key: 'time_investment_score', name: '时间投入' },
        { key: 'skill_requirements_score', name: '技能要求' }
      ],
      发展特征得分: [
        { key: 'industry_lifecycle_score', name: '行业生命周期' },
        { key: 'salary_growth_score', name: '薪资增长' },
        { key: 'job_adaptation_score', name: '岗位适配性' }
      ],
      风险特征得分: [
        { key: 'industry_risk_score', name: '行业风险' },
        { key: 'difficulty_of_transformation_score', name: '转型难度' },
        { key: 'alternative_risk_score', name: '替代风险' }
      ],
      适配特征得分: [
        { key: 'interest_matching_score', name: '兴趣匹配' },
        { key: 'lifestyle_adaptation_score', name: '生活方式适配' }
      ]
    }
  }

  /**
   * 扩展桑基图数据，添加属性、分属性、细分项列
   * @param {Object} sankeyData - 基础桑基图数据
   * @param {Array} subspecialtyList - 子专业列表
   * @returns {Object} 扩展后的桑基图数据
   */
  async extendSankeyDataWithAttributes(sankeyData, subspecialtyList) {
    const extendedNodes = [...sankeyData.nodes]
    const extendedLinks = [...sankeyData.links]
    
    // 获取映射关系
    const subAttributeMapping = this.getSubAttributeMapping()
    const detailedItemsMapping = this.getDetailedItemsMapping()
    
    // 添加三大属性节点
    const attributeNodes = ['形态属性', '功能属性', '特征属性']
    attributeNodes.forEach(attr => {
      if (!extendedNodes.find(n => n.name === attr)) {
        extendedNodes.push({ name: attr })
      }
    })
    
    // 添加分属性节点（11项）
    const subAttributeNodes = [
      '概念类型', '内容载体', '抽象程度定义',
      '需求满足得分', '条件适配得分', '场景对接得分', '数据支撑得分',
      '学习特征得分', '发展特征得分', '风险特征得分', '适配特征得分'
    ]
    subAttributeNodes.forEach(attr => {
      if (!extendedNodes.find(n => n.name === attr)) {
        extendedNodes.push({ name: attr })
      }
    })
    
    // 添加细分项节点（22项）
    const detailedItemNodes = [
      '就业导向', '升学导向', '兴趣导向', '分数适配', '学科优势适配', '资源背景适配',
      '地域场景对接', '行业场景对接', '政策场景对接', '就业数据支撑', '升学数据支撑',
      '学习难度', '时间投入', '技能要求', '行业生命周期', '薪资增长', '岗位适配性',
      '行业风险', '转型难度', '替代风险', '兴趣匹配', '生活方式适配'
    ]
    detailedItemNodes.forEach(item => {
      if (!extendedNodes.find(n => n.name === item)) {
        extendedNodes.push({ name: item })
      }
    })
    
    // 计算第三列（具体专业）到属性列的总权重，用于归一化
    let totalWeightToAttributes = 0
    const tempLinks = []
    
    // 为每个具体专业创建到属性的连接
    let addedLinks = 0
    for (const subspecialty of subspecialtyList) {
      const subspecialtyName = subspecialty.sub_name.trim()
      const scoreDetails = this.parseScoreDetails(subspecialty.score_details)
      
      if (!scoreDetails) {
        continue
      }
      
      const analysis = this.analyzeAttributes(scoreDetails)
      if (!analysis) {
        continue
      }
      
      const morphScore = analysis.morphological.score || 1
      const funcScore = analysis.functional.score || 1
      const featScore = analysis.feature.score || 1
      
      totalWeightToAttributes += morphScore + funcScore + featScore
      
      // 临时存储，稍后归一化
      tempLinks.push({
        source: subspecialtyName,
        target: '形态属性',
        value: morphScore
      })
      tempLinks.push({
        source: subspecialtyName,
        target: '功能属性',
        value: funcScore
      })
      tempLinks.push({
        source: subspecialtyName,
        target: '特征属性',
        value: featScore
      })
      
      addedLinks += 3
    }
    
    // 计算第三列的总流入量（用于归一化）
    const thirdColumnTotal = extendedLinks
      .filter(link => {
        // 找到所有流入第三列的连接
        return extendedNodes.some(n => n.name === link.target && 
          !attributeNodes.includes(link.target) &&
          !subAttributeNodes.includes(link.target) &&
          !detailedItemNodes.includes(link.target))
      })
      .reduce((sum, link) => sum + link.value, 0)
    
    // 归一化：让流向属性列的总量等于第三列的总流入量
    const normalizationFactor = thirdColumnTotal > 0 && totalWeightToAttributes > 0
      ? thirdColumnTotal / totalWeightToAttributes
      : 1
    
    // 应用归一化并添加到扩展连接中
    tempLinks.forEach(link => {
      extendedLinks.push({
        ...link,
        value: link.value * normalizationFactor
      })
    })
    
    // 连接：属性 → 分属性（使用均匀分配）
    const attrToSubAttrWeight = thirdColumnTotal / 3 // 三个属性平均分配
    
    // 形态属性的分属性
    extendedLinks.push(
      { source: '形态属性', target: '概念类型', value: attrToSubAttrWeight / 3 },
      { source: '形态属性', target: '内容载体', value: attrToSubAttrWeight / 3 },
      { source: '形态属性', target: '抽象程度定义', value: attrToSubAttrWeight / 3 }
    )
    
    // 功能属性的分属性
    extendedLinks.push(
      { source: '功能属性', target: '需求满足得分', value: attrToSubAttrWeight / 4 },
      { source: '功能属性', target: '条件适配得分', value: attrToSubAttrWeight / 4 },
      { source: '功能属性', target: '场景对接得分', value: attrToSubAttrWeight / 4 },
      { source: '功能属性', target: '数据支撑得分', value: attrToSubAttrWeight / 4 }
    )
    
    // 特征属性的分属性
    extendedLinks.push(
      { source: '特征属性', target: '学习特征得分', value: attrToSubAttrWeight / 4 },
      { source: '特征属性', target: '发展特征得分', value: attrToSubAttrWeight / 4 },
      { source: '特征属性', target: '风险特征得分', value: attrToSubAttrWeight / 4 },
      { source: '特征属性', target: '适配特征得分', value: attrToSubAttrWeight / 4 }
    )
    
    // 连接：分属性 → 细分项（保持流量守恒）
    const subAttrWeight = attrToSubAttrWeight / 3 // 每个形态属性的分属性
    
    extendedLinks.push(
      { source: '需求满足得分', target: '就业导向', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '需求满足得分', target: '升学导向', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '需求满足得分', target: '兴趣导向', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '条件适配得分', target: '分数适配', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '条件适配得分', target: '学科优势适配', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '条件适配得分', target: '资源背景适配', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '场景对接得分', target: '地域场景对接', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '场景对接得分', target: '行业场景对接', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '场景对接得分', target: '政策场景对接', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '数据支撑得分', target: '就业数据支撑', value: (attrToSubAttrWeight / 4) / 2 },
      { source: '数据支撑得分', target: '升学数据支撑', value: (attrToSubAttrWeight / 4) / 2 },
      
      { source: '学习特征得分', target: '学习难度', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '学习特征得分', target: '时间投入', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '学习特征得分', target: '技能要求', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '发展特征得分', target: '行业生命周期', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '发展特征得分', target: '薪资增长', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '发展特征得分', target: '岗位适配性', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '风险特征得分', target: '行业风险', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '风险特征得分', target: '转型难度', value: (attrToSubAttrWeight / 4) / 3 },
      { source: '风险特征得分', target: '替代风险', value: (attrToSubAttrWeight / 4) / 3 },
      
      { source: '适配特征得分', target: '兴趣匹配', value: (attrToSubAttrWeight / 4) / 2 },
      { source: '适配特征得分', target: '生活方式适配', value: (attrToSubAttrWeight / 4) / 2 }
    )
    
    const result = {
      nodes: extendedNodes,
      links: extendedLinks,
      scoreField: sankeyData.scoreField
    }
    
    return result
  }
}

// 创建单例实例
const sankeyDataService = new SankeyDataService()

export default sankeyDataService
